import React, {useState} from "react";
import PropTypes from "prop-types";
import {FormattedMessage, useIntl} from "react-intl";

import {Alert, TextField} from "@mui/material";
import SendIcon from "@mui/icons-material/Send";

import StyledCard from "components/card/StyledCard";
import SaveButton from "components/button/SaveButton";
import BeatLoaderMini from "components/loaders/BeatLoaderMini";

import {greyishBlue600} from "assets/jss/main";

import {formatDateTime} from "views/personalShopper/components/formatters";

ReplyBox.propTypes = {
    conversation: PropTypes.object.isRequired,
    heldByMe: PropTypes.bool.isRequired,
    onReply: PropTypes.func.isRequired,
};

/**
 * Writes to the shopper. The message leaves through the shop's WhatsApp number; the shopper
 * sees it as any other message of the assistant. Past WhatsApp's 24-hour window it leaves
 * through the shop's Meta-approved template when it has one, else writing is refused.
 * Enter sends, Shift+Enter breaks the line.
 */
function ReplyBox({conversation, heldByMe, onReply}) {
    const intl = useIntl();
    const [text, setText] = useState("");
    const [sending, setSending] = useState(false);
    const [error, setError] = useState(null);

    // Past WhatsApp's 24-hour window the text leaves through the shop's Meta-approved template, when it has one.
    const pastWindow = !conversation.customerServiceWindowOpen;
    const canWrite = heldByMe && (!pastWindow || !!conversation.followUpTemplate);

    const send = () => {
        const trimmed = text.trim();
        if (trimmed === "" || sending || !canWrite) {
            return;
        }
        setSending(true);
        setError(null);
        onReply(trimmed)
            .then(() => setText(""))
            .catch((requestError) => setError(requestError.message))
            .finally(() => setSending(false));
    };

    return (
        <StyledCard noHoverShadow>
            {!heldByMe && (
                <div style={{color: greyishBlue600, fontSize: "13px", marginBottom: "8px"}}>
                    <FormattedMessage id="personalShopper.reply.takeOverFirst" />
                </div>
            )}
            {heldByMe && pastWindow && !conversation.followUpTemplate && (
                <Alert severity="warning" sx={{marginBottom: "8px"}}>
                    <FormattedMessage id="personalShopper.reply.windowClosed" values={{date: formatDateTime(conversation.lastInboundAt)}} />
                </Alert>
            )}
            {heldByMe && pastWindow && conversation.followUpTemplate && (
                <Alert severity="info" sx={{marginBottom: "8px"}}>
                    <FormattedMessage id="personalShopper.reply.windowClosedTemplate" values={{date: formatDateTime(conversation.lastInboundAt), template: conversation.followUpTemplate}} />
                </Alert>
            )}
            <TextField
                fullWidth
                multiline
                minRows={2}
                maxRows={6}
                value={text}
                disabled={!canWrite || sending}
                placeholder={intl.formatMessage({id: "personalShopper.reply.placeholder"})}
                onChange={(event) => setText(event.target.value)}
                onKeyDown={(event) => {
                    if (event.key === "Enter" && !event.shiftKey) {
                        event.preventDefault();
                        send();
                    }
                }}
                sx={{"& .MuiOutlinedInput-root": {borderRadius: "8px", fontSize: "14px"}}}
            />
            <div style={{display: "flex", alignItems: "center", gap: "12px", marginTop: "8px"}}>
                <SaveButton disabled={!canWrite || sending || text.trim() === ""} onClick={send} startIcon={<SendIcon />}>
                    <FormattedMessage id="personalShopper.reply.send" />
                    <BeatLoaderMini loading={sending} />
                </SaveButton>
                <span style={{color: greyishBlue600, fontSize: "12px"}}><FormattedMessage id="personalShopper.reply.hint" /></span>
                {error && <span style={{color: "#F70D1B", fontSize: "13px"}}>{error}</span>}
            </div>
        </StyledCard>
    );
}

export default ReplyBox;
